import { OPR_SESSION_TOKEN } from "@/constants";
import { createFrameTransformStream } from "@/services/stream";
import type { QunjielongActivityDetailData, QunjielongDetailTaskStatus } from "@/services/module/qunjielongAccount";
import type { CompetitorCollectedMaterial } from "@/pages/_layout/goods/-goodsDetail/CompetitorMaterialContext";
import type { ProductDetailData } from "@/data-contracts";

export type CopywritingLength = "short" | "medium" | "long";

// 风格配置 payload：前端透传给 agent，agent 只认这些字段不认 styleId
export type CopywritingStylePayload = {
  systemPrompt: string;
  examples: Array<{ good: string }>;
  length: CopywritingLength;
};

export type CopywritingGenerateRequest = {
  requestId?: string;
  product: ProductDetailData & {
    activityPrice?: number | string;
    priceText?: string;
    privateServiceRatio?: number;
    selectedSku?: {
      price?: number;
      specification?: string;
    };
  };
  competitorMaterials?: Array<{
    collected: CompetitorCollectedMaterial;
    qunjielongDetail?: QunjielongActivityDetailData & {
      status?: QunjielongDetailTaskStatus;
    };
  }>;
  style: CopywritingStylePayload;
};

export type CopywritingDraftImage = {
  materialId: string;
  imageUrl: string;
  source: "cover" | "activity_detail";
  caption?: string;
  reason: string;
};

export type CopywritingText = {
  id: string;
  title: string;
  body: string;
};

export type CopywritingDraft = CopywritingText & {
  imagePlan: CopywritingDraftImage[];
};

export type CopywritingStreamPhase =
  | "preparing"
  | "prepared"
  | "generating"
  | "streaming"
  | "recommending_images"
  | "images_ready"
  | "completed";

export type CopywritingPhaseMeta = {
  requestId?: string;
  materialCount?: number;
  warnings?: string[];
  imageCount?: number;
};

export type CopywritingProgressStep =
  | "cleaning_material"
  | "extracting_insights"
  | "building_prompt"
  | "waiting_llm"
  | "scoring_images";

export type CopywritingProgressMeta = {
  requestId?: string;
  index?: number;
  total?: number;
  materialId?: string;
  materialTitle?: string;
  elapsedMs?: number;
};

// SSE 事件类型，与 agent 端 CopywritingStreamEvent 对齐
export type CopywritingStreamEvent =
  | {
      type: "phase";
      phase: CopywritingStreamPhase;
      message: string;
      meta?: CopywritingPhaseMeta;
    }
  | {
      type: "progress";
      step: CopywritingProgressStep;
      message: string;
      meta?: CopywritingProgressMeta;
    }
  | { type: "delta"; field: "title" | "body"; chunk: string }
  | { type: "thinking_delta"; chunk: string }
  | { type: "copywriting_done"; draft: CopywritingText }
  | {
      type: "image_recommend";
      imageUrl: string;
      reason: string;
      source: "cover" | "activity_detail";
      materialId?: string;
      caption?: string;
    }
  | { type: "done"; draft: CopywritingDraft }
  | { type: "error"; error: string };

export function getAgentBaseUrl() {
  const configured = process.env.ENV_REPLACE_COPYWRITING_AGENT_URL?.trim();
  if (configured) return configured.replace(/\/$/, "");
  throw new Error("未配置文案生成 Agent 地址");
}

export function getCopywritingGenerateUrl() {
  return `${getAgentBaseUrl()}/copywriting/generate`;
}

export function getOptimizePromptUrl() {
  return `${getAgentBaseUrl()}/copywriting/optimize-prompt`;
}

export function getCopywritingAuthHeader() {
  return window.localStorage.getItem(OPR_SESSION_TOKEN) || "";
}

// SSE 流解析：把原始字节流转换成一个个 CopywritingStreamEvent
// agent 端格式：`event: <type>\ndata: <json>\n\n`，data 本身已含 type 字段
// 注意：@ant-design/x-sdk 的 XStream 内部已通过 TextDecoderStream 把 Uint8Array 转成 string，
// 所以这里的 transformStream 输入是 string，不需要自己再做解码。
export function createSseTransformStream() {
  return createFrameTransformStream<CopywritingStreamEvent>("\n\n", parseSseBlock);
}

function parseSseBlock(block: string): CopywritingStreamEvent | null {
  const lines = block.split("\n").filter(Boolean);
  let dataStr = "";
  for (const line of lines) {
    if (line.startsWith("data: ")) {
      dataStr += line.slice(6);
    }
  }
  if (!dataStr) return null;
  try {
    return JSON.parse(dataStr) as CopywritingStreamEvent;
  } catch {
    return null;
  }
}

export type OptimizePromptMode = "full_rewrite";

export type OptimizePromptRequest = {
  length: CopywritingLength;
  systemPrompt: string;
  mode?: OptimizePromptMode;
};

// 优化提示词接口的 SSE 事件
export type OptimizePromptStreamEvent =
  | { type: "meta"; mode: OptimizePromptMode; replaceTarget: "full" }
  | { type: "delta"; delta: string }
  | { type: "done" }
  | { type: "error"; error: string };

// 优化提示词接口的 SSE 解析：格式为 `data: {"type":"delta","delta":"chunk"}\n\n`
export function createOptimizePromptTransformStream() {
  return createFrameTransformStream<OptimizePromptStreamEvent>("\n\n", parseOptimizePromptSseBlock);
}

function parseOptimizePromptSseBlock(block: string): OptimizePromptStreamEvent | null {
  const lines = block.split("\n").filter(Boolean);
  let dataStr = "";
  for (const line of lines) {
    if (line.startsWith("data: ")) {
      dataStr += line.slice(6);
    }
  }
  if (!dataStr) return null;
  try {
    const parsed = JSON.parse(dataStr) as Record<string, unknown>;
    if (parsed.type === "meta" || parsed.type === "delta" || parsed.type === "done" || parsed.type === "error") {
      return parsed as OptimizePromptStreamEvent;
    }
    if (typeof parsed.delta === "string") {
      return { type: "delta", delta: parsed.delta };
    }
    if (typeof parsed.error === "string") {
      return { type: "error", error: parsed.error };
    }
    return null;
  } catch {
    return null;
  }
}

const copywritingAgentApis = {
  getCopywritingGenerateUrl,
  getOptimizePromptUrl,
  getCopywritingAuthHeader,
  createSseTransformStream,
  createOptimizePromptTransformStream,
};

export default copywritingAgentApis;
